import * as React from "react";
import { Button } from "../ui/Button";
import { useSound } from "../../audio/useSound";
import { text, useContent } from "../../hooks/useContent";

/**
 * Persistent icon-only sound toggle, mounted once globally (App.js) so it
 * appears bottom-right on every route. Reflects whether sound is actually audible:
 * because browsers block audio until the first user gesture, the toggle reads
 * "off" on load and flips to "on" once the first interaction unlocks audio. From
 * then on it mutes/unmutes everything through the shared master gain — reading-
 * flow effects and the ambient background loop alike. Renders nothing while the
 * audio engine has no loaded buffers (no enabled, file-backed sounds configured
 * in Django admin yet) so no orphan affordance appears.
 */
export function MuteToggle() {
  const { isMuted, started, hasAnySound, toggleMute, startAudio } = useSound();
  const { data: content } = useContent();

  if (!hasAnySound) return null;

  const muteLabel = text(content, "reading.sound.mute_label");
  const unmuteLabel = text(content, "reading.sound.unmute_label");

  // "On" only when audio is actually playing: unlocked by a gesture and unmuted.
  const soundOn = started && !isMuted;

  return (
    // Keep the toggle's own press away from the engine's window-level "first
    // gesture" listener: it unlocks audio on any of pointerdown/pointerup/
    // touchend, all of which precede this button's click. If one slipped
    // through, the click would then read started=true and *mute* instead of
    // starting. The toggle owns its own gesture — onClick starts audio the
    // first time, then toggles mute thereafter.
    <div
      className="fixed bottom-4 right-4 z-50"
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
    >
      <Button
        variant="icon"
        surface="floating"
        icon={soundOn ? "sound-on" : "sound-off"}
        ariaLabel={soundOn ? muteLabel : unmuteLabel}
        onClick={started ? toggleMute : startAudio}
      />
    </div>
  );
}
